/**
 * Dashboard quick links — personal edit mode: drag to reorder, eye toggle to
 * hide/show, custom links (name + URL). Saved as one JSON preference via
 * POST /dashboard/quicklinks; the page reloads after save/reset/cancel so
 * the server-rendered list is the single source of truth.
 */
export function setupDashboardQuickLinks() {
    const list = document.getElementById('quickList');
    const editBtn = document.getElementById('quickEditBtn');
    if (!list || !editBtn) return;
    // 요소당 한 번만: 관리자 화면(/admin/quicklinks)은 이 편집기를 페이지 모듈로도
    // 등록해 두었고(main.js registerAdminPage), main.js 는 모든 화면에서 한 번 더
    // 부른다 — 두 번 붙으면 편집 토글이 곧바로 되돌아간다. 제자리 교체로 들어온
    // 새 #quickList 에는 표식이 없으니 그때는 정상적으로 다시 붙는다.
    if (list.dataset.quickReady === '1') return;
    list.dataset.quickReady = '1';
    const tools = document.getElementById('quickEditTools');
    const saveBtn = document.getElementById('quickSaveBtn');
    const cancelBtn = document.getElementById('quickCancelBtn');
    const resetBtn = document.getElementById('quickResetBtn');
    const addForm = document.getElementById('quickAddForm');
    // 같은 편집기를 대시보드(개인 설정)와 관리자 기본값 페이지가 함께 쓴다.
    const endpoint = list.dataset.endpoint || '/dashboard/quicklinks';
    const customPrefix = list.dataset.customPrefix || 'custom:';
    let editing = false;

    function items() {
        return Array.from(list.querySelectorAll('.dash-quick-item'));
    }
    function toast(msg, error) {
        if (window.showNotification) window.showNotification(msg, error ? 'error' : 'success');
    }
    function setToggleLabel(item) {
        const btn = item.querySelector('.dash-quick-toggle');
        if (!btn) return;
        const hidden = item.dataset.hidden === '1';
        btn.title = hidden ? (list.dataset.labelShow || 'Show') : (list.dataset.labelHide || 'Hide');
        btn.classList.toggle('is-off', hidden);
    }

    function enterEdit() {
        editing = true;
        list.classList.add('editing');
        editBtn.hidden = true;
        tools.hidden = false;
        if (addForm) addForm.hidden = false;
        items().forEach((item) => {
            item.hidden = false; // 숨긴 항목도 편집 중엔 흐리게 보인다
            item.draggable = true;
            const ctl = item.querySelector('.dash-quick-ctl');
            if (ctl) ctl.hidden = false;
            setToggleLabel(item);
        });
    }
    editBtn.addEventListener('click', enterEdit);
    cancelBtn.addEventListener('click', () => window.location.reload());

    // 링크 클릭은 편집 중엔 이동하지 않는다.
    list.addEventListener('click', (e) => {
        if (!editing) return;
        const link = e.target.closest('.dash-quick-link');
        if (link) {
            e.preventDefault();
            return;
        }
        const toggle = e.target.closest('.dash-quick-toggle');
        if (toggle) {
            const item = toggle.closest('.dash-quick-item');
            item.dataset.hidden = item.dataset.hidden === '1' ? '0' : '1';
            item.classList.toggle('is-hidden', item.dataset.hidden === '1');
            setToggleLabel(item);
            return;
        }
        const rm = e.target.closest('.dash-quick-remove');
        if (rm) rm.closest('.dash-quick-item').remove();
    });

    // ── 드래그 정렬 (HTML5 DnD, 가로 flex-wrap: 대상의 가운데 기준 앞/뒤) ──
    let dragging = null;
    list.addEventListener('dragstart', (e) => {
        const item = e.target.closest('.dash-quick-item');
        if (!editing || !item) return;
        dragging = item;
        item.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', item.dataset.key); } catch (_) { /* IE */ }
    });
    list.addEventListener('dragover', (e) => {
        if (!dragging) return;
        e.preventDefault();
        const over = e.target.closest('.dash-quick-item');
        if (!over || over === dragging) return;
        const r = over.getBoundingClientRect();
        const before = e.clientX < r.left + r.width / 2;
        over.parentNode.insertBefore(dragging, before ? over : over.nextSibling);
    });
    list.addEventListener('dragend', () => {
        if (dragging) dragging.classList.remove('dragging');
        dragging = null;
    });

    // ── 직접 링크 추가 ──
    if (addForm) {
        addForm.addEventListener('submit', (e) => {
            e.preventDefault();
            const name = addForm.elements.name.value.trim();
            let url = addForm.elements.url.value.trim();
            if (!name || !url) return;
            if (!/^(https?:\/\/|\/)/.test(url)) url = 'https://' + url;
            const id = 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
            const item = document.createElement('div');
            item.className = 'dash-quick-item is-custom';
            item.dataset.key = customPrefix + id;
            item.dataset.hidden = '0';
            item.dataset.name = name;
            item.dataset.url = url;
            item.draggable = true;
            const a = document.createElement('a');
            a.className = 'dash-quick-link';
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener';
            const icon = document.createElement('span');
            icon.className = 'dash-quick-icon';
            const tpl = list.querySelector('.dash-quick-item.is-custom .dash-quick-icon');
            icon.innerHTML = tpl ? tpl.innerHTML : '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>';
            const label = document.createElement('span');
            label.className = 'dash-quick-label';
            label.textContent = name;
            a.appendChild(icon);
            a.appendChild(label);
            const ctl = document.createElement('span');
            ctl.className = 'dash-quick-ctl';
            const toggle = list.querySelector('.dash-quick-toggle');
            ctl.innerHTML = (toggle ? toggle.outerHTML : '') + '<button type="button" class="dash-quick-remove" aria-label="remove">✕</button>';
            item.appendChild(a);
            item.appendChild(ctl);
            list.appendChild(item);
            setToggleLabel(item);
            addForm.reset();
            addForm.elements.name.focus();
        });
    }

    // ── 저장 / 기본값 ──
    function post(body) {
        const send = window.csrfFetch || fetch;
        return send(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
        }).then((r) => {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
        });
    }
    saveBtn.addEventListener('click', () => {
        const order = items().map((i) => i.dataset.key);
        const hidden = items().filter((i) => i.dataset.hidden === '1').map((i) => i.dataset.key);
        const custom = items().filter((i) => i.classList.contains('is-custom')).map((i) => ({
            id: i.dataset.key.slice(customPrefix.length), name: i.dataset.name || '', url: i.dataset.url || '',
        }));
        saveBtn.disabled = true;
        post({ order, hidden, custom })
            .then(() => { toast(list.dataset.msgSaved || 'Saved'); window.location.reload(); })
            .catch(() => { toast(list.dataset.msgFailed || 'Could not save', true); saveBtn.disabled = false; });
    });
    resetBtn.addEventListener('click', () => {
        resetBtn.disabled = true;
        post({ reset: true })
            .then(() => window.location.reload())
            .catch(() => { toast(list.dataset.msgFailed || 'Could not save', true); resetBtn.disabled = false; });
    });
}
